import React from 'react';
import { Activity } from 'lucide-react';
import { AudioVisualizer } from './AudioVisualizer';

interface StudioRecordBarProps {
  isRecording: boolean;
  countdown: number | null;
  isUploadingTake: boolean;
  hasSelectedLines: boolean;
  liveAnalyserNode: AnalyserNode | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onSetupHotMicMonitoring: () => void;
}

export const StudioRecordBar: React.FC<StudioRecordBarProps> = ({
  isRecording,
  countdown,
  isUploadingTake,
  hasSelectedLines,
  liveAnalyserNode,
  onStartRecording,
  onStopRecording,
  onSetupHotMicMonitoring
}) => {
  const isCapturing = isRecording || countdown !== null;
  const canRecord = hasSelectedLines || isCapturing;

  return (
    <>
      {/* Red Recording / Black Stop Action Button: Directly under video window, same width as player */}
      <div className="studio-stage-rec-col">
        <button
          type="button"
          onClick={isCapturing ? onStopRecording : (canRecord ? onStartRecording : undefined)}
          disabled={isUploadingTake || !canRecord}
          className={`studio-rec-btn ${isCapturing ? 'is-capturing' : ''} ${!canRecord ? 'is-disabled' : ''}`}
          style={
            !canRecord
              ? {
                  opacity: 0.45,
                  filter: 'grayscale(1)',
                  cursor: 'not-allowed',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.35)',
                  boxShadow: 'none'
                }
              : undefined
          }
          title={
            !hasSelectedLines && !isCapturing
              ? 'Select one or more dialogue lines in the teleprompter to record'
              : isCapturing
              ? 'Click to stop capture and playback immediately'
              : 'Click to record take (● REC)'
          }
        >
          {isCapturing ? (
            <span className="rec-btn-label">STOP</span>
          ) : (
            <>
              <span className="rec-dot" style={{ color: canRecord ? '#ef4444' : '#64748b' }}>●</span>
              <span className="rec-btn-label" style={{ color: canRecord ? '#ffffff' : 'rgba(255,255,255,0.4)' }}>REC</span>
            </>
          )}
        </button>
      </div>

      {/* Mic Response Waveform Window: Spans the rest of the width to the right */}
      <div
        className="studio-stage-mic-col"
        onClick={hasSelectedLines ? onSetupHotMicMonitoring : undefined}
        style={{
          cursor: !hasSelectedLines ? 'not-allowed' : (!liveAnalyserNode ? 'pointer' : 'default'),
          opacity: hasSelectedLines ? 1 : 0.65
        }}
        title={
          !hasSelectedLines
            ? 'Microphone is OFF. Select dialogue lines in teleprompter to activate.'
            : (!liveAnalyserNode ? 'Click to enable microphone input monitor' : 'Live microphone input monitor active')
        }
      >
        <div className="mic-waveform-inner">
          <AudioVisualizer
            analyserNode={hasSelectedLines ? liveAnalyserNode : null}
            isRecording={isCapturing}
            volumeLevel={hasSelectedLines ? 0.8 : 0}
            moodColor="#EF4444"
            height={52}
          />
        </div>
        {!hasSelectedLines && !isUploadingTake && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              color: 'var(--text-muted)',
              fontSize: '0.78rem',
              fontWeight: 600,
              background: 'rgba(10, 15, 30, 0.75)',
              pointerEvents: 'none'
            }}
          >
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#64748b', display: 'inline-block' }} />
            <span>MIC OFF • Arm lines in teleprompter to activate</span>
          </div>
        )}
        {isUploadingTake && (
          <div className="mic-uploading-overlay">
            <Activity size={14} className="spin" color="#c4b5fd" />
            <span>Saving Take to Cloudflare R2...</span>
          </div>
        )}
      </div>
    </>
  );
};
